import { ComponentPropsWithoutRef, FC, ReactNode, Ref, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useFeatureFlagEnabled } from 'posthog-js/react';
import { toast } from 'react-toastify';

import { FEATURE_FLAG_SCREEN_SHARE } from '../../../constants/featureFlags';
import { CloseCall } from '../../icons/close-call';
import { MicroIcon } from '../../icons/micro';
import { Settings } from '../../icons/settings';
import { VideoIcon } from '../../icons/video';
import { WarningIcon } from '../../icons/warning';
import { BlurIcon } from '../../icons/blur';
import { ScreenShareIcon } from '../../icons/screen-share';
import { GroupParticipantsPanelIcon } from '../../icons/group-participants-panel';
import { MeetChatIcon } from '../../icons/meet-chat';
import { EllipsisThreeDotsButtonIcon } from '../../icons/ellipsis-three-dots-button';
import { Supervisor } from '../../icons/supervisor';
import { Accept } from '../../icons/accept';
import { CloseCrossBtn } from '../../icons/close-cross-btn';
import { Reject } from '../../icons/reject';
import { Information } from '../../icons/information';
import MobilePopup from '../../common/mobile-popup/mobile-popap';
import { ActionMenuPopup } from '../../common/action-menu-popup';
import ModalWrap from '../../common/modal-wrap';
import Button from '../../common/button';
import { RaiseHandIcon } from '../../icons/raise-hand';

type SupervisorNotification =
  | { type: 'request_sent' }
  | { type: 'accepted' }
  | { type: 'rejected' }
  | { type: 'incoming_request' };

export interface ISettingPanelProps {
  audioMute: boolean;
  showMicWarning: boolean;
  showCamWarning: boolean;
  onAudioMuteClick: () => void;
  videoMute: boolean;
  onVideoMuteClick: () => void;
  onOpenSettingsClick: () => void;
  leaveRoom: () => void;
  isLocalActiveSpeaker: boolean;
  onBackgroundBlurClick: () => void;
  backgroundMode: 'none' | 'blur' | 'image';
  isBackgroundSupported: boolean;
  onSupervisorClick?: () => void;
  statusData: boolean;
  isClientConnected: boolean;
  isPsychologist: boolean;
  /** Групповая терапия: текущий участник назначен соорганизатором (видит кнопку «Участники») */
  isCoOrganizer?: boolean;
  isTooltipVisible: boolean;
  supervisorNotification?: SupervisorNotification | null;
  onCloseNotification?: () => void;
  supervisorSend: boolean;
  setSupervisorSend: (send: boolean) => void;
  sendSupervisorResponse?: (accepted: boolean) => void;
  isNotificationClosed: boolean;
  onNotificationClose: () => void;
  isSupervisorAllowed: boolean;
  isSupervisorLoading: boolean;
  supervisorRemaining?: number;
  isSupervisorBalanceWarning: boolean;
  startScreenShare?: () => void;
  stopScreenShare?: () => void;
  isScreenSharing?: boolean;
  isGroupCall?: boolean;
  onMacCardsClick?: () => void;
  /** Групповая терапия: показывать кнопку «Участники группы» в нижней панели */
  isGroupConsultation?: boolean;
  onGroupParticipantsClick?: () => void;
  onRaisedHandClick?: () => void;
  /** Групповая терапия: панель участников открыта — выделить кнопку */
  groupParticipantsPanelOpen?: boolean;
  /** Групповая терапия: число ожидающих подключение (для отображения бейджа на иконке участников) */
  groupParticipantsAwaitingCount?: number;
  /** Групповая терапия: действия при выборе пунктов из меню «три точки» */
  onGroupMoreOptionEmotions?: () => void;
  onGroupMoreOptionMacCards?: () => void;
  /** Групповая терапия (психолог): завершить конференцию для всех */
  onEndConferenceForAll?: () => void;
  /** Групповая терапия (психолог): только выйти из конференции */
  onLeaveConference?: () => void;
  isRaisedHandActive?: boolean;
  showRaisedHandButton?: boolean;
  chatPanelOpen?: boolean;
  /** Есть непрочитанные сообщения в чате */
  chatHasUnread?: boolean;
  onChatClick?: () => void;
}

const panelSectionClassName = 'flex h-full min-w-0 items-center';
const panelGroupClassName = 'flex min-w-0 items-center gap-4';
const panelIconButtonClassName =
  'inline-flex h-[40px] w-[40px] shrink-0 cursor-pointer items-center justify-center rounded-[10px] align-middle transition-colors duration-150 disabled:cursor-not-allowed';

interface PanelControlButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'ref'> {
  icon: ReactNode;
  buttonRef?: Ref<HTMLButtonElement>;
  overlay?: ReactNode;
  wrapperClassName?: string;
}

const PanelControlButton: FC<PanelControlButtonProps> = ({
  icon,
  buttonRef,
  overlay,
  wrapperClassName,
  className,
  type = 'button',
  ...buttonProps
}) => (
  <div className={classNames('relative shrink-0', wrapperClassName)}>
    <button ref={buttonRef} type={type} className={classNames(panelIconButtonClassName, className)} {...buttonProps}>
      {icon}
    </button>
    {overlay}
  </div>
);

export const SettingPanel: FC<ISettingPanelProps> = ({
  audioMute,
  videoMute,
  onVideoMuteClick,
  onAudioMuteClick,
  onOpenSettingsClick,
  leaveRoom,
  showMicWarning,
  showCamWarning,
  isLocalActiveSpeaker,
  onBackgroundBlurClick,
  backgroundMode,
  isBackgroundSupported,
  onSupervisorClick,
  statusData,
  isClientConnected,
  isPsychologist,
  isCoOrganizer = false,
  isTooltipVisible,
  supervisorNotification,
  supervisorSend,
  sendSupervisorResponse,
  setSupervisorSend,
  isNotificationClosed,
  onNotificationClose,
  isSupervisorAllowed,
  isSupervisorLoading,
  supervisorRemaining,
  isSupervisorBalanceWarning,
  startScreenShare,
  stopScreenShare,
  isScreenSharing = false,
  isGroupCall = false,
  onMacCardsClick,
  isGroupConsultation = false,
  onGroupParticipantsClick,
  onRaisedHandClick,
  groupParticipantsPanelOpen = false,
  groupParticipantsAwaitingCount = 0,
  onGroupMoreOptionEmotions,
  onGroupMoreOptionMacCards,
  onEndConferenceForAll,
  onLeaveConference,
  isRaisedHandActive = false,
  showRaisedHandButton = false,
  chatPanelOpen = false,
  chatHasUnread = false,
  onChatClick,
}) => {
  const isVirtualBackground = useFeatureFlagEnabled('virtual-background');
  const isScreenShareEnabled = useFeatureFlagEnabled(FEATURE_FLAG_SCREEN_SHARE);

  const [localResponse, setLocalResponse] = useState<'accepted' | 'rejected' | null>(null);
  const [groupMoreOptionsOpen, setGroupMoreOptionsOpen] = useState(false);
  const [groupMoreOptionsPosition, setGroupMoreOptionsPosition] = useState<{ bottom: number; right: number } | null>(
    null,
  );
  const [mobileMoreOptionsOpen, setMobileMoreOptionsOpen] = useState(false);
  const [mobileMoreOptionsPosition, setMobileMoreOptionsPosition] = useState<{ bottom: number; right: number } | null>(
    null,
  );
  const [endCallMenuOpen, setEndCallMenuOpen] = useState(false);
  const [endCallMenuPosition, setEndCallMenuPosition] = useState<{ bottom: number; right: number } | null>(null);
  const [endConferenceConfirmOpen, setEndConferenceConfirmOpen] = useState(false);
  const groupMoreOptionsTriggerRef = useRef<HTMLButtonElement>(null);
  const groupMoreOptionsContentRef = useRef<HTMLDivElement>(null);
  const mobileMoreOptionsTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileMoreOptionsContentRef = useRef<HTMLDivElement>(null);
  const endCallButtonRef = useRef<HTMLButtonElement>(null);
  const endCallMenuContentRef = useRef<HTMLDivElement>(null);
  const showEndCallChoice = Boolean(onEndConferenceForAll && onLeaveConference);

  useLayoutEffect(() => {
    if (!groupMoreOptionsOpen) {
      setGroupMoreOptionsPosition(null);
      return;
    }
    const el = groupMoreOptionsTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setGroupMoreOptionsPosition({
      bottom: window.innerHeight - rect.top + 4,
      right: window.innerWidth - rect.right,
    });
  }, [groupMoreOptionsOpen]);

  useLayoutEffect(() => {
    if (!endCallMenuOpen) {
      setEndCallMenuPosition(null);
      return;
    }
    const el = endCallButtonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setEndCallMenuPosition({
      bottom: window.innerHeight - rect.top + 4,
      right: window.innerWidth - rect.right,
    });
  }, [endCallMenuOpen]);

  useLayoutEffect(() => {
    if (!mobileMoreOptionsOpen) {
      setMobileMoreOptionsPosition(null);
      return;
    }
    const el = mobileMoreOptionsTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMobileMoreOptionsPosition({
      bottom: window.innerHeight - rect.top + 4,
      right: window.innerWidth - rect.right,
    });
  }, [mobileMoreOptionsOpen]);

  useEffect(() => {
    if (!groupMoreOptionsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        groupMoreOptionsTriggerRef.current?.contains(target) ||
        groupMoreOptionsContentRef.current?.contains(target)
      ) {
        return;
      }
      setGroupMoreOptionsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [groupMoreOptionsOpen]);

  useEffect(() => {
    if (!endCallMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (endCallButtonRef.current?.contains(target) || endCallMenuContentRef.current?.contains(target)) return;
      setEndCallMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [endCallMenuOpen]);

  useEffect(() => {
    if (!mobileMoreOptionsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        mobileMoreOptionsTriggerRef.current?.contains(target) ||
        mobileMoreOptionsContentRef.current?.contains(target)
      )
        return;
      setMobileMoreOptionsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMoreOptionsOpen]);

  useEffect(() => {
    if (localResponse) {
      const timer = setTimeout(() => setLocalResponse(null), 5000);

      return () => clearTimeout(timer);
    }
  }, [localResponse]);

  //скрываемм через 5с уведомления мобильные

  useEffect(() => {
    if (isPsychologist && supervisorNotification && !isNotificationClosed) {
      const timer = setTimeout(() => {
        onNotificationClose?.();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isPsychologist, supervisorNotification, isNotificationClosed]);

  const handleCloseNotification = () => {
    onNotificationClose?.();
  };

  const renderDesktopNotification = (placeToRightOfButton?: boolean) => {
    if (isNotificationClosed) return null;

    const positionClasses = placeToRightOfButton
      ? 'left-[calc(100%-20px)] bottom-[calc(100%+8px)] max-w-[380px]'
      : 'bottom-[calc(100%+10px)] right-[1px]';

    if (!isPsychologist && supervisorSend) {
      return (
        <div
          className={classNames(
            'flex absolute z-[60] gap-3 px-4 py-[15px] text-xs font-medium text-white rounded-xl border bg-[#f0f8ff] border-[#1E90FF] justify-between w-[440px]',
            positionClasses,
          )}
        >
          <div className="flex gap-3">
            <Information className="mt-[2px] flex-shrink-0" />
            <div className="flex flex-col text-[#000]">
              <span className="font-medium text-base mb-[8px]">Повышение качества консультаций.</span>
              <span className="text-sm font-normal">
                Разрешите анализ сессии ИИ-супервизору. Это поможет вашему психологу улучшать качество помощи.
              </span>
              <div className="flex gap-[10px] mt-2 text-sm">
                <button
                  onClick={() => {
                    sendSupervisorResponse?.(true);
                    setSupervisorSend(false);
                    handleCloseNotification();
                    setLocalResponse('accepted');
                  }}
                  className="cursor-pointer w-[90px] h-10 rounded-[10px] text-[#fff] bg-[#0065FF] "
                >
                  Разрешить
                </button>
                <button
                  onClick={() => {
                    sendSupervisorResponse?.(false);
                    setSupervisorSend(false);
                    handleCloseNotification();
                    setLocalResponse('rejected');
                  }}
                  className="cursor-pointer w-[90px] h-10 rounded-[10px] text-[#585858]"
                >
                  Отклонить
                </button>
              </div>
            </div>
          </div>
          <button className="cursor-pointer h-0">
            <CloseCrossBtn fill="#99A0AC" width={11} height={11} />
          </button>
        </div>
      );
    }

    if (supervisorNotification) {
      const { type } = supervisorNotification;

      return (
        <div
          className={classNames(
            'flex absolute w-[440px] z-[60] gap-3 px-4 py-[15px] text-xs font-medium text-white rounded-[5px] sm:rounded-xl border just justify-between',
            positionClasses,
            {
              'bg-[#f0f8ff] border-[#1E90FF]': type === 'request_sent',
              'bg-[#eafcf1] border-[#1BA049]': type === 'accepted',
              'bg-[#ffefef] border-[#D92020]': type === 'rejected',
            },
            // 📱 Мобильные
            'max-sm:fixed max-sm:top-4 max-sm:left-1/2 max-sm:-translate-x-1/2 max-sm:bottom-auto max-sm:right-auto max-sm:bg-white max-sm:border-[#E5E7EB] max-sm:text-[14px] max-sm:py-1 max-sm:px-3 max-sm:w-auto max-sm:max-w-[90%] max-sm:inline-flex max-sm:min-h-[40px] max-sm:items-center',
          )}
        >
          <div className="flex gap-3">
            {type === 'request_sent' && <Information className="mt-[2px] flex-shrink-0" />}
            {type === 'accepted' && <Accept className="mt-[2px]" />}
            {type === 'rejected' && <Reject className="mt-[2px]" />}
            <div className="flex flex-col text-[#000]">
              <span className="font-medium text-base sm:text-base max-sm:text-[14px] whitespace-nowrap">
                <span className="hidden max-sm:inline">
                  {type === 'request_sent' && 'Запрос отправлен'}
                  {type === 'accepted' && 'Запрос принят'}
                  {type === 'rejected' && 'Запрос отклонён'}
                </span>
                <span className="max-sm:hidden">
                  {type === 'request_sent' && 'Запрос отправлен.'}
                  {type === 'accepted' && 'Запрос принят.'}
                  {type === 'rejected' && 'Запрос отклонён'}
                </span>
              </span>
              <span className="text-sm font-normal max-sm:hidden">
                {type === 'request_sent' && 'Ожидаем разрешение Клиента на анализ сессии ИИ-супервизором.'}
                {type === 'accepted' && 'ИИ-анализ появится в Терапевтическом журнале через 10 минут после сессии.'}
                {type === 'rejected' && 'Анализ сессии проводиться не будет.'}
              </span>
            </div>
          </div>
          <button className="cursor-pointer h-0 max-sm:hidden" onClick={handleCloseNotification}>
            <CloseCrossBtn fill="#99A0AC" width={11} height={11} />
          </button>
        </div>
      );
    }

    return null;
  };

  const renderClientResponse = () => {
    if (isPsychologist || !localResponse) return null;

    const isAccepted = localResponse === 'accepted';

    return (
      <>
        {/* Десктоп */}
        <div
          className={classNames(
            'hidden sm:flex absolute bottom-[calc(100%+10px)] right-[1px] w-[380px] z-[60] gap-3 px-4 py-[15px] text-xs font-medium text-white rounded-xl border  justify-between ',
            isAccepted ? 'bg-[#eafcf1] border-[#1BA049]' : 'bg-[#EBF3FF] border-[#0065FF]',
          )}
        >
          <div className="flex gap-3">
            {isAccepted ? (
              <Accept className="mt-[2px] flex-shrink-0" />
            ) : (
              <Information className="mt-[2px] flex-shrink-0" />
            )}
            <div className="flex flex-col text-[#000]">
              <span className="text-sm font-normal">
                {isAccepted
                  ? 'Вы разрешили использование ИИ-супервизора.'
                  : 'Вы отклонили запрос на использование ИИ-супервизора.'}
              </span>
            </div>
          </div>
        </div>

        {/* Мобильные */}
        <div
          className={classNames(
            'sm:hidden fixed top-4 left-1/2 -translate-x-1/2 w-auto max-w-[90%] px-3 py-2 rounded-[5px] flex items-center gap-3 bg-white text-[#000] z-[60]',
          )}
        >
          {isAccepted ? <Accept /> : <Information />}
          <span className="text-sm">{isAccepted ? 'Запрос принят' : 'Запрос отклонен'}</span>
        </div>
      </>
    );
  };

  const renderSupervisorPermissionPopup = () => {
    if (!supervisorSend) {
      return null;
    }

    return (
      <MobilePopup
        acceptText="Отклонить"
        rejectText="Разрешить"
        title="Повышение качества консультаций."
        description="Разрешите анализ сессии ИИ-супервизору. Это поможет вашему психологу улучшать качество помощи."
        onCancel={() => {
          sendSupervisorResponse?.(false);
          setSupervisorSend(false);
          handleCloseNotification();
          setLocalResponse('rejected');
        }}
        onRequest={() => {
          sendSupervisorResponse?.(true);
          setSupervisorSend(false);
          handleCloseNotification();
          setLocalResponse('accepted');
        }}
      />
    );
  };

  const hasSupervisorBalance = typeof supervisorRemaining === 'number' && Number.isFinite(supervisorRemaining);
  const formattedSupervisorRemaining = hasSupervisorBalance ? Math.max(0, Math.ceil(supervisorRemaining)) : undefined;
  const supervisorPurchaseUrl = '/subscriptions?tab=plans';
  const purchaseMinutesLink = (
    <a
      href={supervisorPurchaseUrl}
      target="_blank"
      rel="noreferrer"
      className="font-semibold text-[#0065FF] underline underline-offset-2 hover:text-[#0047B3]"
    >
      Купить минуты
    </a>
  );
  const isSupervisorUnavailable = !isSupervisorAllowed || !isClientConnected;
  const canInteractWithSupervisor = isPsychologist && !isSupervisorUnavailable;
  const shouldRenderSupervisorButton = isPsychologist || statusData;
  const supervisorTooltip = (() => {
    if (!isPsychologist) {
      return 'ИИ-супервизор';
    }

    if (formattedSupervisorRemaining !== undefined && formattedSupervisorRemaining <= 10) {
      return (
        <>
          <span>
            Доступно {formattedSupervisorRemaining} мин. При балансе 10 и менее минут ИИ-ассистенты недоступны.
          </span>
          {purchaseMinutesLink}
        </>
      );
    }

    if (!isSupervisorAllowed) {
      return 'Необходимо подключить тариф \nс опцией ИИ Супервизор';
    }

    if (!isClientConnected) {
      return 'Подождите когда подключится клиент, чтобы активировать ИИ-супервизор';
    }

    if (formattedSupervisorRemaining !== undefined && formattedSupervisorRemaining < 20) {
      return (
        <>
          <span>Доступно {formattedSupervisorRemaining} мин.</span>
          {purchaseMinutesLink}
        </>
      );
    }

    return formattedSupervisorRemaining === undefined
      ? 'ИИ-супервизор'
      : `Доступно ${formattedSupervisorRemaining} мин.`;
  })();

  const activeSupervisorTooltip = isSupervisorBalanceWarning ? (
    <>
      <span>Минуты ИИ-ассистентов заканчиваются. ИИ-супервизор скоро будет отключён.</span>
      {purchaseMinutesLink}
    </>
  ) : (
    supervisorTooltip
  );

  const handleSupervisorButtonClick = () => {
    if (!canInteractWithSupervisor) return;
    onSupervisorClick?.();
  };

  const renderSupervisorTooltip = (placeToRightOfButton?: boolean) => {
    if (!isPsychologist) {
      return null;
    }

    if (isTooltipVisible) {
      return (
        <div
          className={classNames(
            'flex items-center justify-center absolute border-1 border-[#0065FF] bottom-full bg-[#EBF3FF] w-[230px] h-[48px] text-sm font-normal rounded-md whitespace-nowrap z-[60]',
            placeToRightOfButton ? 'left-[calc(100%-20px)]' : 'right-0',
          )}
        >
          <div className="flex gap-3 items-center">
            <Information />
            <span>ИИ-супервизор отключен</span>
          </div>
        </div>
      );
    }

    return null;
  };

  const supervisorIconClassName = classNames('w-[40px] h-[40px]', {
    'opacity-50 grayscale': isSupervisorUnavailable || isSupervisorLoading,
  });

  const chatControl = onChatClick ? (
    <PanelControlButton
      onClick={onChatClick}
      aria-label={chatPanelOpen ? 'Закрыть чат' : 'Открыть чат'}
      aria-pressed={chatPanelOpen}
      icon={<MeetChatIcon className="w-[40px] h-[40px]" checked={chatPanelOpen} />}
      overlay={
        chatHasUnread && !chatPanelOpen ? (
          <span
            aria-hidden
            className="pointer-events-none absolute -right-[3px] -top-[3px] h-[14px] w-[14px] rounded-full border-2 border-white bg-[#E11D48]"
          />
        ) : null
      }
    />
  ) : null;

  const audioControl = (
    <PanelControlButton
      onClick={onAudioMuteClick}
      icon={<MicroIcon className="w-[40px] h-[40px]" checked={audioMute} isSpeaking={isLocalActiveSpeaker} />}
      overlay={showMicWarning ? <WarningIcon className="absolute -top-[5px] -left-[10px] w-6 h-6" /> : null}
    />
  );

  const videoControl = (
    <PanelControlButton
      onClick={onVideoMuteClick}
      icon={<VideoIcon className="w-[40px] h-[40px]" checked={videoMute} />}
      overlay={showCamWarning ? <WarningIcon className="absolute -top-[5px] -left-[10px] w-6 h-6" /> : null}
    />
  );

  const screenShareControl = (
    <PanelControlButton
      onClick={() => {
        if (isScreenSharing) {
          stopScreenShare?.();
        } else {
          startScreenShare?.();
        }
      }}
      icon={<ScreenShareIcon className="w-[40px] h-[40px]" checked={isScreenSharing} />}
    />
  );

  const raisedHandControl = showRaisedHandButton ? (
    <PanelControlButton
      onClick={() => onRaisedHandClick?.()}
      aria-label={isRaisedHandActive ? 'Опустить руку' : 'Поднять руку'}
      aria-pressed={isRaisedHandActive}
      icon={<RaiseHandIcon width={40} height={40} className="w-[40px] h-[40px]" checked={isRaisedHandActive} />}
    />
  ) : null;

  const groupParticipantsControl =
    (isPsychologist || isCoOrganizer) && isGroupConsultation ? (
      <PanelControlButton
        onClick={onGroupParticipantsClick}
        aria-label="Участники группы"
        icon={
          <GroupParticipantsPanelIcon
            width={40}
            height={40}
            className="w-[40px] h-[40px]"
            showBadge={groupParticipantsAwaitingCount > 0}
            checked={groupParticipantsPanelOpen}
          />
        }
      />
    ) : null;

  const groupMoreOptionsControl =
    isGroupConsultation && isPsychologist && !isCoOrganizer ? (
      <PanelControlButton
        buttonRef={groupMoreOptionsTriggerRef}
        onClick={() => setGroupMoreOptionsOpen((value) => !value)}
        aria-label="Ещё"
        aria-expanded={groupMoreOptionsOpen}
        icon={<EllipsisThreeDotsButtonIcon width={40} height={40} className="w-[40px] h-[40px]" />}
      />
    ) : null;

  const regularSupervisorControl =
    !isGroupConsultation && shouldRenderSupervisorButton ? (
      <PanelControlButton
        onClick={handleSupervisorButtonClick}
        // Не используем disabled для ограничения по балансу: ссылка в hover должна
        // оставаться кликабельной и открывать покупку минут в новой вкладке.
        disabled={isSupervisorLoading}
        aria-disabled={!canInteractWithSupervisor || isSupervisorLoading}
        icon={
          <Supervisor
            isPsychologist={canInteractWithSupervisor}
            statusData={statusData}
            className={supervisorIconClassName}
            tooltip={supervisorTooltip}
            activeTooltip={activeSupervisorTooltip}
            warning={isSupervisorBalanceWarning}
            noTopMargin
          />
        }
        overlay={
          <>
            <div className="sm:block">{isPsychologist && renderDesktopNotification()}</div>
            {isPsychologist && renderSupervisorTooltip()}
          </>
        }
      />
    ) : null;

  const blurControl = isVirtualBackground && isBackgroundSupported ? (
    <PanelControlButton
      onClick={onBackgroundBlurClick}
      aria-pressed={backgroundMode === 'blur'}
      aria-label={backgroundMode === 'blur' ? 'Выключить размытие фона' : 'Размытие фона'}
      icon={<BlurIcon className="w-[40px] h-[40px]" checked={backgroundMode === 'blur'} />}
    />
  ) : null;

  const clientNotificationDesktopContent = !isPsychologist ? (
    <div className="relative hidden sm:block">
      {renderDesktopNotification()}
      {renderClientResponse()}
    </div>
  ) : null;

  const mobileNotificationContent = (
    <div className="sm:hidden">
      {isPsychologist ? renderDesktopNotification() : null}
      {!isPsychologist ? renderClientResponse() : null}
      {!isPsychologist ? renderSupervisorPermissionPopup() : null}
    </div>
  );

  const leftContent = isGroupConsultation ? (
    <>
      {chatControl}
      {audioControl}
      {videoControl}
    </>
  ) : (
    <>{chatControl}</>
  );

  const centerContent = isGroupConsultation ? (
    <>
      {screenShareControl}
      {raisedHandControl}
      {groupParticipantsControl}
      {groupMoreOptionsControl}
      {blurControl}
    </>
  ) : (
    <>
      {audioControl}
      {videoControl}
      {screenShareControl}
      {blurControl}
    </>
  );

  const rightContent = (
    <>
      {regularSupervisorControl}
      {clientNotificationDesktopContent}
      <PanelControlButton onClick={onOpenSettingsClick} icon={<Settings className="w-[40px] h-[40px]" />} />
      <PanelControlButton
        buttonRef={showEndCallChoice ? endCallButtonRef : undefined}
        onClick={showEndCallChoice ? () => setEndCallMenuOpen(true) : leaveRoom}
        icon={<CloseCall className="w-[40px] h-[40px]" />}
      />
    </>
  );

  const mobileMoreMenuItems = [
    ...(isScreenShareEnabled
      ? [
          {
            label: isScreenSharing ? 'Остановить демонстрацию экрана' : 'Демонстрация экрана',
            onClick: () => {
              setMobileMoreOptionsOpen(false);
              if (isScreenSharing) {
                stopScreenShare?.();
                return;
              }

              startScreenShare?.();
            },
          },
        ]
      : []),
    ...(showRaisedHandButton && onRaisedHandClick
      ? [
          {
            label: isRaisedHandActive ? 'Опустить руку' : 'Поднять руку',
            onClick: () => {
              setMobileMoreOptionsOpen(false);
              onRaisedHandClick();
            },
          },
        ]
      : []),
    ...((isPsychologist || isCoOrganizer) && isGroupConsultation && onGroupParticipantsClick
      ? [
          {
            label: 'Участники группы',
            onClick: () => {
              setMobileMoreOptionsOpen(false);
              onGroupParticipantsClick();
            },
          },
        ]
      : []),
    ...(isVirtualBackground && isBackgroundSupported
      ? [
          {
            label: backgroundMode === 'blur' ? 'Выключить размытие фона' : 'Размытие фона',
            onClick: () => {
              setMobileMoreOptionsOpen(false);
              onBackgroundBlurClick();
            },
          },
        ]
      : []),
    ...(shouldRenderSupervisorButton && !isGroupConsultation
      ? [
          {
            label: 'ИИ-супервизор',
            onClick: () => {
              setMobileMoreOptionsOpen(false);
              handleSupervisorButtonClick();
            },
          },
        ]
      : []),
    ...(isGroupConsultation && onGroupMoreOptionEmotions
      ? [
          {
            label: 'Детектор эмоций',
            onClick: () => {
              setMobileMoreOptionsOpen(false);
              onGroupMoreOptionEmotions();
            },
          },
        ]
      : []),
    ...(isGroupConsultation && onGroupMoreOptionMacCards
      ? [
          {
            label: 'МАК-карты',
            onClick: () => {
              setMobileMoreOptionsOpen(false);
              onGroupMoreOptionMacCards();
            },
          },
        ]
      : []),
    {
      label: 'Настройки',
      onClick: () => {
        setMobileMoreOptionsOpen(false);
        onOpenSettingsClick();
      },
    },
  ];

  const mobilePrimaryControls = (
    <>
      {chatControl}
      {audioControl}
      {videoControl}
      <PanelControlButton
        buttonRef={mobileMoreOptionsTriggerRef}
        onClick={() => setMobileMoreOptionsOpen((value) => !value)}
        aria-label="Ещё"
        aria-expanded={mobileMoreOptionsOpen}
        icon={<EllipsisThreeDotsButtonIcon width={40} height={40} className="w-[40px] h-[40px]" />}
      />
      <PanelControlButton
        buttonRef={showEndCallChoice ? endCallButtonRef : undefined}
        onClick={showEndCallChoice ? () => setEndCallMenuOpen(true) : leaveRoom}
        icon={<CloseCall className="w-[40px] h-[40px]" />}
      />
    </>
  );

  const sideSectionClassName = isGroupCall ? 'px-6' : 'px-2';

  return (
    <div
      className={classNames(
        'grid h-full w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center',
        isGroupCall ? 'bg-transparent' : 'bg-white',
      )}
    >
      <div className="col-span-3 flex h-full w-full items-center justify-center gap-3 px-2 sm:hidden">
        {mobilePrimaryControls}
      </div>

      <div className={classNames(panelSectionClassName, 'hidden justify-start sm:flex', sideSectionClassName)}>
        <div className={panelGroupClassName}>{leftContent}</div>
      </div>

      <div className={classNames(panelSectionClassName, 'hidden justify-center px-4 sm:flex')}>
        <div className={panelGroupClassName}>{centerContent}</div>
      </div>

      <div className={classNames(panelSectionClassName, 'hidden justify-end sm:flex', sideSectionClassName)}>
        <div className={panelGroupClassName}>{rightContent}</div>
      </div>

      {mobileNotificationContent}

      {showEndCallChoice && endConferenceConfirmOpen && (
        <ModalWrap isShow={true}>
          <div className="think-scroll flex flex-col gap-6 w-full max-w-[90%] sm:max-w-[60%] md:max-w-[560px] h-auto rounded-[20px] p-6 lg:p-10 bg-white shadow-shadowPopup">
            <div className="w-full flex justify-between items-start">
              <h2 className="font-comfortaa text-lg lg:text-2xl text-[#111026] font-bold">
                Завершить встречу для всех
              </h2>
              <button
                type="button"
                className="cursor-pointer"
                onClick={() => setEndConferenceConfirmOpen(false)}
                aria-label="Закрыть"
              >
                <CloseCrossBtn width={20} height={20} />
              </button>
            </div>
            <p className="text-sm md:text-base font-bold text-[#585858]">
              При выполнении этого действия встреча будет завершена для всех участников встречи.
            </p>
            <div className="flex justify-end gap-4">
              <Button
                type="grey"
                className="!w-max px-4 py-2 !text-sm md:!text-base"
                onClick={() => setEndConferenceConfirmOpen(false)}
              >
                Отмена
              </Button>
              <Button
                type="black"
                className="!w-max px-4 py-2 !text-sm md:!text-base"
                onClick={() => {
                  setEndConferenceConfirmOpen(false);
                  onEndConferenceForAll?.();
                }}
              >
                Завершить встречу для всех
              </Button>
            </div>
          </div>
        </ModalWrap>
      )}

      {showEndCallChoice && endCallMenuOpen && endCallMenuPosition && (
        <ActionMenuPopup
          contentRef={endCallMenuContentRef}
          position={endCallMenuPosition}
          width={330}
          items={[
            {
              label: 'Завершить конференцию для всех',
              onClick: () => {
                setEndCallMenuOpen(false);
                setEndConferenceConfirmOpen(true);
              },
            },
            {
              label: 'Выйти из конференции',
              onClick: () => {
                setEndCallMenuOpen(false);
                onLeaveConference?.();
              },
            },
          ]}
        />
      )}

      {isGroupConsultation && groupMoreOptionsOpen && groupMoreOptionsPosition && (
        <ActionMenuPopup
          contentRef={groupMoreOptionsContentRef}
          position={groupMoreOptionsPosition}
          items={[
            {
              label: 'Детектор эмоций',
              onClick: () => {
                onGroupMoreOptionEmotions?.();
                setGroupMoreOptionsOpen(false);
              },
            },
            {
              label: 'МАК-карты',
              onClick: () => {
                onGroupMoreOptionMacCards?.();
                setGroupMoreOptionsOpen(false);
              },
            },
            {
              label: 'Т-игра (Скоро)',
              onClick: () => {
                toast.info('Скоро');
                setGroupMoreOptionsOpen(false);
              },
            },
          ]}
        />
      )}

      {mobileMoreOptionsOpen && mobileMoreOptionsPosition && (
        <ActionMenuPopup
          contentRef={mobileMoreOptionsContentRef}
          position={mobileMoreOptionsPosition}
          width={260}
          items={mobileMoreMenuItems}
        />
      )}
    </div>
  );
};
